import React, { useState, useEffect, useCallback } from "react";
import { getBanners } from "../api/bannerApi";
import { BannerShimmer } from "./ShimmerEffect";

/**
 * CarouselSlider
 * Full-width image carousel for the home page.
 * - Fetches banners from /api/laptops/banners
 * - Auto-advances every 4 seconds
 * - Manual prev/next arrows
 * - Dot indicators
 * - Shows BannerShimmer while loading
 * - Returns null if no banners after load (zero layout impact)
 */
const CarouselSlider = () => {
  const [banners, setBanners] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Fetch banners on mount
  useEffect(() => {
    const fetchBanners = async () => {
      const result = await getBanners();
      setBanners(result.banners || []);
      setLoading(false);
    };
    fetchBanners();
  }, []);

  // Go to a specific slide
  const goTo = useCallback(
    (index) => {
      if (isTransitioning || banners.length === 0) return;
      setIsTransitioning(true);
      setCurrentIndex(
        ((index % banners.length) + banners.length) % banners.length,
      );
      setTimeout(() => setIsTransitioning(false), 500);
    },
    [banners.length, isTransitioning],
  );

  const goNext = useCallback(
    () => goTo(currentIndex + 1),
    [currentIndex, goTo],
  );
  const goPrev = useCallback(
    () => goTo(currentIndex - 1),
    [currentIndex, goTo],
  );

  // Auto-scroll every 7 seconds
  useEffect(() => {
    if (banners.length <= 1) return;
    const interval = setInterval(goNext, 7000);
    return () => clearInterval(interval);
  }, [banners.length, goNext]);

  // ── Show shimmer while fetching ──────────────────────────────
  if (loading) return <BannerShimmer />;

  // No banners available after load → render nothing
  if (banners.length === 0) return null;

  return (
    <section
      className="relative w-full overflow-hidden"
      style={{ aspectRatio: "16/5" }}
    >
      {/* Slides — crossfade */}
      <div className="relative w-full h-full">
        {banners.map((banner, idx) => (
          <div
            key={banner._id || idx}
            className="absolute inset-0 w-full h-full"
            style={{
              opacity: idx === currentIndex ? 1 : 0,
              transition: "opacity 1.2s cubic-bezier(0.4, 0, 0.2, 1)",
              zIndex: idx === currentIndex ? 1 : 0,
            }}
          >
            <img
              src={banner.imageUrl}
              alt={`Promotional banner ${idx + 1}`}
              style={{
                width: "100%",
                height: "100%",
                display: "block",
                objectFit: "cover",
                objectPosition: "center",
              }}
              loading="lazy"
            />
          </div>
        ))}
      </div>

      {/* Prev / Next arrows removed as per user request */}

      {/* Dot indicators (only show when there's more than 1 banner) */}
      {banners.length > 1 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-2 z-10">
          {banners.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goTo(idx)}
              aria-label={`Go to banner ${idx + 1}`}
              className={`rounded-full transition-all duration-300 ${
                idx === currentIndex
                  ? "w-5 h-2 bg-white"
                  : "w-2 h-2 bg-white/50 hover:bg-white/80"
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default CarouselSlider;
